import {SearchSelect} from "../../classes/search-select/search-select";
import {enableAllInputs} from "./form-utils";

/**
 * Loads event listeners to different form elements on the page.
 */
export function loadForms() {
    initInputs();
    initFileInputs();
    initSwitchInputs();
    initCheckboxInputs();
    initSearchSelectInputs();
    applyEnableAllInputsForForms();
}

const inputSelector = '.m-input-layout input[type=text], ' +
    '.m-input-layout input[type=password], ' +
    '.m-input-layout textarea';

const fileSelector = '.m-input-file';
const switchSelector = '.m-switch';
const checkboxSelector = '.m-checkbox-2, .m-radio-2';

function initInputs() {
    function onInputChange(el, inputPrefix) {
        if (el.value !== '') {
            if (inputPrefix) inputPrefix.classList.add('has-value');
            el.classList.add('has-value');
        } else {
            if (inputPrefix) inputPrefix.classList.remove('has-value');
            el.classList.remove('has-value');
        }
    }

    const inputs = document.querySelectorAll(inputSelector);
    if (inputs) {
        inputs.forEach((el, idx) => {
            const parent = el.parentElement;
            const inputPrefix = parent ? parent.querySelector('.input-prefix') : null;
            const label = parent ? parent.querySelector('label') : null;

            el.addEventListener('change', function (evt) {
                onInputChange(this, inputPrefix);
            });

            if (inputPrefix) {
                el.addEventListener('focus', function (evt) {
                    inputPrefix.classList.add('is-editing');
                });
                el.addEventListener('focusout', function (evt) {
                    inputPrefix.classList.remove('is-editing');
                });
            }

            setupClickToCopyLogicForInput(el);

            if (label) {
                label.addEventListener('click', function () {
                    el.focus();
                });
            }

            onInputChange(el, inputPrefix);
        });
    }
}

function setupClickToCopyLogicForInput(el) {
    const parent = el.parentElement;
    const { readOnly } = el.dataset;

    const floatingLabel = parent.querySelector('.floating-label');
    if (floatingLabel) {
        if (readOnly === 'true') {
            parent.classList.add('read-only-copyable');

            const floatingLabelText = floatingLabel.innerText;
            const copiedText = `${floatingLabelText} ${i18n.form.copied}`;

            parent.addEventListener('mouseover', () => {
                floatingLabel.innerText = `${floatingLabelText} ${i18n.form.clickToCopy}`;
                clearTimeout(resetFloatingLabel);
            });
            parent.addEventListener('mouseout', () => {
                if (floatingLabel.innerText === copiedText) {
                    return;
                }
                floatingLabel.innerText = floatingLabelText;
            });

            parent.addEventListener('click', () => {
                floatingLabel.innerText = copiedText;
                navigator.clipboard.writeText(el.value);
                setTimeout(resetFloatingLabel, 3000);
            });

            function resetFloatingLabel() {
                if (floatingLabel.innerText !== copiedText) {
                    return;
                }
                floatingLabel.innerText = floatingLabelText;
            }
        }
    }
}

function initFileInputs() {
    const inputs = document.querySelectorAll(fileSelector);
    for (let i = 0; i < inputs.length; i++) {
        const btn = inputs[i].querySelector('.btn');
        const fileInput = inputs[i].querySelector('input[type=file]');
        const textInput = inputs[i].querySelector('input[type=text]');
        if (!btn || !fileInput || !textInput)
            continue;

        if (fileInput.disabled) { // this may not work when toggling disabled...
            textInput.disabled = true;
            btn.classList.add('disabled');
        }

        fileInput.addEventListener('change', function(evt) {
            const files = evt.target.files;
            if (files.length > 0) {
                let fileStr = '';
                for (let j = 0; j < files.length; j++) {
                    fileStr += files[i].name;
                    if (j < files.length - 1)
                        fileStr += ', ';
                }
                textInput.value = fileStr;
            } else {
                textInput.value = '';
            }

        });
    }
}

function initSwitchInputs() {
    const switches = document.querySelectorAll(switchSelector);
    for (let i = 0; i < switches.length; i++) {
        const zSw = switches[i];

        const zCb = zSw.querySelector('input[type=checkbox]');
        const zThumb = zSw.querySelector('.thumb');
        const zTrack = zSw.querySelector('.track');
        if (!zThumb || !zTrack || !zCb)
            continue;

        zThumb.addEventListener('mousedown', drag);

        function drag(evt) {
            if (zCb.disabled) {
                evt.stopPropagation();
                return false;
            }

            let delta = 1;
            let downX = evt.clientX - parseInt(zThumb.style.left);
            let upX;

            document.addEventListener('mousemove', mouseMove);
            document.addEventListener('mouseup', mouseUp);

            function mouseUp(evt) {
                document.removeEventListener('mousemove', mouseMove);
                document.removeEventListener('mouseup', mouseUp);
                if (Math.abs(upX - downX) > delta) {
                    if (upX < 5) {
                        zCb.checked = false;
                        zThumb.style.left = '';
                    } else if (upX >= 5) {
                        zCb.checked = true;
                        zThumb.style.left = '';
                    }
                } else {
                    zCb.checked = !zCb.checked;
                }
            }

            function mouseMove(evt) {
                const diff = evt.clientX - downX;
                upX = diff < -6 ? -6 : diff > 16 ? 16 : diff;
                zThumb.style.left = upX + 'px';
            }
        }

        zTrack.addEventListener('click', function (evt) {
            if (zCb.disabled) {
                evt.stopPropagation();
                return false;
            }
            zCb.checked = !zCb.checked;
        });
    }
}

function initCheckboxInputs() {
    const cbEls = document.querySelectorAll(checkboxSelector);
    if (!cbEls)
        return;

    for (let i = 0; i < cbEls.length; i++) {
        const cbEl = cbEls[i];
        const hiddenCbEls = cbEl.querySelector('input[type=checkbox], input[type=radio]');
        if (!hiddenCbEls)
            continue;

        for (let j = 0; j < hiddenCbEls.length; j++) {
            const hiddenCb = hiddenCbEls[j];
            hiddenCb.addEventListener('focus', function(evt) {
                cbEl.classList.add('hover');
            });
            hiddenCb.addEventListener('blur', function(evt) {
                cbEl.classList.remove('hover');
            });
            hiddenCb.addEventListener('change', function(evt) {
                if (hiddenCb.checked)
                    cbEl.classList.add('checked');
                else
                    cbEl.classList.remove('checked');
            });
        }
    }
}

/**
 * Ent Module will automatically apply searchSelect logic.
 - If you want to use a custom SearchSelect implementation in a custom
 JS file for a page (eg to set a custom onChange handler, like in the
 IR Links Add Edit page, set the 'useCustom' flag as true in csp:searchSelect.tag.
 */
function initSearchSelectInputs() {
    const containers = document.querySelectorAll('.search-select');
    if (containers) {
        const { csrfToken } = GlobalEnt.pageData;
        if (!csrfToken)
            return;

        containers.forEach((container) => {
            const { useCustom } = container.dataset;
            if (useCustom)
                return;

            const ajaxUrl = container.dataset.ajaxUrl || document.location;
            new SearchSelect(container, ajaxUrl, csrfToken, null);
        });
    }
}

function applyEnableAllInputsForForms() {
    document.querySelectorAll('form').forEach(form => {
        /**
         * To apply this logic, you will want to add the following attribute
         * to the form element: data-enable-all-inputs="true"
         */
        const shouldEnableAllInputs = form.dataset.enableAllInputs;
        if (shouldEnableAllInputs) {
            form.addEventListener('submit', function () {
                enableAllInputs(form);
                return true;
            });
        }
    });

}

export function hasFormInputs() {
    return document.querySelector(inputSelector
        + ', ' + fileSelector + ', ' + switchSelector + ', ' + checkboxSelector);
}